import React, { useEffect, useMemo } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useTranslation } from '../../../utils/translations';
import usePartnerData from '../../../hooks/usePartnerData';
import { getConfig } from '../../../config/environment';
import {
  invalidateSelectedPartner,
  normalizePartnerSelection,
  resolvePartnerFromList,
} from '../../../utils/partnerSelection';
import CheckoutPartnerSelection from '../../../components/checkout/CheckoutPartnerSelection';
import { usePurchasePage } from './PurchasePageContext';

export default function PurchasePartnerSelectorSection() {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const {
    selectedPartner,
    setSelectedPartner,
    purchasePagePartners,
    purchasePageFieldLabel,
    purchasePageDisplayConfig,
    purchasePagePartnersLoading,
    partnerRequiredOnPurchase,
    siteDetails,
    siteLoading,
  } = usePurchasePage();

  const { tlfId } = getConfig();
  const organizationId = siteDetails?.organizationId || siteDetails?.OrganizationId || siteDetails?.id || tlfId || null;

  // Use hook as fallback if context did not fetch partner data yet
  const hookData = usePartnerData(
    null,
    !purchasePagePartners,
    organizationId,
    false,
    null,
    'PurchasePage'
  );

  const rawPartners = purchasePagePartners || hookData.partners;
  const fieldLabel = purchasePageFieldLabel || hookData.fieldLabel;
  const displayConfig = purchasePageDisplayConfig || hookData.displayConfig;
  const loading = purchasePagePartners ? purchasePagePartnersLoading : hookData.loading;

  const normalizedPartners = useMemo(
    () => (rawPartners || [])
      .map(normalizePartnerSelection)
      .filter((partner) => partner?.id && partner?.name),
    [rawPartners]
  );

  const fieldRequired = useMemo(() => {
    if (typeof partnerRequiredOnPurchase === 'boolean') return partnerRequiredOnPurchase;
    if (!displayConfig?.requiredOn) return false;
    return displayConfig.requiredOn.some(
      (s) => String(s).toLowerCase() === 'purchasepage'
    );
  }, [partnerRequiredOnPurchase, displayConfig]);

  const partnerRequired = useMemo(() => {
    if (fieldRequired) return true;
    return normalizedPartners.some((partner) => {
      const cfg = partner?.displayConfig;
      if (!cfg) return false;
      const parsed = typeof cfg === 'string' ? JSON.parse(cfg) : cfg;
      return (parsed?.requiredOn || []).some(
        (s) => String(s).toLowerCase() === 'purchasepage'
      );
    });
  }, [fieldRequired, normalizedPartners]);

  useEffect(() => {
    if (siteLoading || loading || normalizedPartners.length === 0) return;
    if (!selectedPartner) return;
    const resolvedPartner = resolvePartnerFromList(normalizedPartners, selectedPartner);
    if (!resolvedPartner) {
      invalidateSelectedPartner(selectedPartner);
      setSelectedPartner(null);
    } else if (
      String(resolvedPartner.id) !== String(selectedPartner?.id || '') ||
      !selectedPartner?.name
    ) {
      setSelectedPartner(resolvedPartner);
    }
  }, [siteLoading, loading, normalizedPartners, selectedPartner, setSelectedPartner]);

  // If displayAs is set to Hidden, don't show the selector on public purchase forms
  if (displayConfig?.displayAs === 'Hidden') return null;

  if (!siteLoading && !loading && normalizedPartners.length === 0) return null;

  return (
    <div className="relative z-30">
      <CheckoutPartnerSelection
        partners={normalizedPartners}
        loading={siteLoading || loading}
        selectedPartner={selectedPartner}
        onPartnerChange={setSelectedPartner}
        fieldLabel={fieldLabel}
        required={partnerRequired}
        variant="light"
        t={t}
      />
    </div>
  );
}
